import type { PreferredContactGap } from '@ehr/shared';
import { ConfirmDialog } from './ConfirmDialog.js';

/** One field a blocked save points to: its section key, its field key and the name shown. */
export type SaveBlockItem = { sec: string; field: string; label: string };

/** Why a save was refused (ehr#179): blank required fields, bad formats, a preferred method gap. */
export type SaveBlock = {
  missing: SaveBlockItem[];
  bad: SaveBlockItem[];
  /** ehr#661: the preferred contact method this save would leave with nothing to reach. */
  prefer?: PreferredContactGap | null;
};

/** The section the preferred contact method fields live in (client chart Contact Info). */
const PREFER_SEC = 'contact';

/**
 * The field "Go to field" opens. With a draft on offer the main button saves the draft, so its
 * "Go to field" goes to the first item listed; without one (or when a draft is refused too) a
 * format problem or the preferred contact gap comes first, as the client chart always did.
 */
export function saveBlockTarget(
  block: SaveBlock,
  draftOffered: boolean,
): { sec: string; field: string } | null {
  const prefer = block.prefer ? { sec: PREFER_SEC, field: block.prefer.field } : null;
  if (draftOffered) return block.bad[0] ?? prefer ?? block.missing[0] ?? null;
  return block.missing[0] ?? block.bad[0] ?? prefer;
}

/**
 * ehr#179 / ehr#760: the "This record was not saved" pop-up. It lists what is missing or in the
 * wrong format, each with a link to its field and the section it is in. Used by the client
 * chart's Save record and by the New Client window, so both say it the same way.
 * Pass onDraft to offer "Save as draft" (only when nothing is malformed: a draft refuses those).
 */
export function SaveBlockDialog({
  block,
  sectionLabel,
  onGoTo,
  onDraft,
  onClose,
  busy = false,
}: {
  block: SaveBlock;
  sectionLabel: (sec: string) => string | undefined;
  onGoTo: (sec: string, field: string) => void;
  onDraft?: () => void;
  onClose: () => void;
  busy?: boolean;
}) {
  const canDraft = !!onDraft && block.bad.length === 0 && !block.prefer;
  const link = (m: SaveBlockItem) => (
    <li key={`${m.sec}.${m.field}`}>
      <button type="button" className="demo-block-link" onClick={() => onGoTo(m.sec, m.field)}>
        {m.label}
      </button>
      <span className="muted"> · {sectionLabel(m.sec)}</span>
    </li>
  );
  const goFirst = () => {
    const t = saveBlockTarget(block, false);
    if (t) onGoTo(t.sec, t.field);
  };
  return (
    <ConfirmDialog
      message={
        <div className="demo-block">
          <p className="demo-block-head">
            <b>This record was not saved.</b>{' '}
            {block.missing.length
              ? 'Some required information is missing.'
              : block.bad.length
                ? 'Some information is not in the right format.'
                : block.prefer?.message}
          </p>
          {block.missing.length > 0 && (
            <>
              <p className="demo-block-sub">Required:</p>
              <ul className="demo-block-list">{block.missing.map(link)}</ul>
            </>
          )}
          {block.bad.length > 0 && (
            <>
              <p className="demo-block-sub">Check the format:</p>
              <ul className="demo-block-list">{block.bad.map(link)}</ul>
            </>
          )}
          {block.prefer && (
            <>
              <p className="demo-block-sub">
                {block.missing.length || block.bad.length
                  ? `${block.prefer.message} ${block.prefer.fillIn}`
                  : block.prefer.fillIn}
              </p>
              <ul className="demo-block-list">
                {[
                  { sec: PREFER_SEC, field: block.prefer.field, label: block.prefer.fieldLabel },
                  { sec: PREFER_SEC, field: 'preferredContact', label: 'Preferred contact method' },
                ].map(link)}
              </ul>
            </>
          )}
          {canDraft && (
            <p className="muted demo-block-foot">
              You can save it as a draft now and finish the required fields later.
            </p>
          )}
        </div>
      }
      danger={false}
      busy={busy}
      confirmLabel={canDraft ? 'Save as draft' : 'Go to field'}
      altLabel={canDraft ? 'Go to field' : undefined}
      cancelLabel="Cancel"
      onAlt={goFirst}
      onConfirm={() => {
        if (canDraft) {
          onDraft!();
          return;
        }
        const t = saveBlockTarget(block, !!onDraft);
        if (t) onGoTo(t.sec, t.field);
      }}
      onClose={onClose}
    />
  );
}
